import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { TrendingUp, Shield, Eye, LogIn, UserPlus, LayoutDashboard } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export const Investors = () => {
  const { t } = useLanguage();
  const { register, login, user } = useAuth();
  const navigate = useNavigate();
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeRef, setActiveRef] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    investment: '',
    terms: false
  });

  // Detect active referral code from localStorage and try to resolve the mediator name
  React.useEffect(() => {
    try {
      const code = localStorage.getItem('loewenherz_ref');
      if (!code) return;
      // Light-weight verification via public endpoint
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/mediators/verify/${code}`;
      fetch(url)
        .then((r) => r.json())
        .then((d) => {
          if (d?.valid) setActiveRef({ code, ...d.mediator });
        })
        .catch(() => {});
    } catch (e) {
      // ignore
    }
  }, [isDialogOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLoginMode) {
      const result = await login({
        email: formData.email,
        password: formData.password
      });
      
      if (result.success) {
        toast.success('Login erfolgreich!');
        setIsDialogOpen(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: '',
          investment: '',
          terms: false
        });
        // Redirect role-based
        const role = result.user?.role;
        if (role === 'investor') navigate('/investor/dashboard');
        else if (role === 'mediator') navigate('/mediator/dashboard');
        else if (role === 'admin' || role === 'editor') navigate('/admin/dashboard');
      } else {
        toast.error(result.error || 'Ungültige Anmeldedaten');
      }
    } else {
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwörter stimmen nicht überein');
        return;
      }
      if (!formData.terms) {
        toast.error('Bitte akzeptieren Sie die Nutzungsbedingungen');
        return;
      }
      
      const refCode = (() => {
        try { return localStorage.getItem('loewenherz_ref') || undefined; } catch { return undefined; }
      })();

      const result = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        investment: parseFloat(formData.investment),
        referralCode: refCode
      });
      
      if (result.success) {
        toast.success('Konto erfolgreich erstellt!');
        // Clear referral after successful registration
        try { localStorage.removeItem('loewenherz_ref'); } catch (e) {}
        setIsDialogOpen(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: '',
          investment: '',
          terms: false
        });
        // New investors go straight to their dashboard
        navigate('/investor/dashboard');
      } else {
        toast.error(result.error || 'Registrierung fehlgeschlagen');
      }
    }
  };

  const benefits = [
    {
      icon: TrendingUp,
      titleKey: 'benefit1',
      descKey: 'benefit1Desc',
      image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBncm93dGh8ZW58MHx8fHwxNzc4MzI4MjY3fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Eye,
      titleKey: 'benefit2',
      descKey: 'benefit2Desc',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3NzgzMjgyNjd8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Shield,
      titleKey: 'benefit3',
      descKey: 'benefit3Desc',
      image: 'https://images.unsplash.com/photo-1519662978799-2f05096d3636'
    }
  ];

  return (
    <section id="investors" className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#C4A55A]/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.investors.title}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t.investors.subtitle}
          </p>
        </div>

        {/* Benefits */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-white text-center mb-12">
            {t.investors.benefits.title}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              const benefitData = t.investors.benefits;
              
              return (
                <Card
                  key={index}
                  className="bg-[#1a1a1a] border-[#C4A55A]/20 overflow-hidden group hover:border-[#C4A55A]/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-[#C4A55A]/20"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={benefit.image}
                      alt={benefitData[benefit.titleKey]}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 rounded-xl bg-[#C4A55A]/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#C4A55A]/30 transition-colors duration-300">
                        <Icon className="h-6 w-6 text-[#C4A55A]" />
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl text-white group-hover:text-[#C4A55A] transition-colors duration-300">
                      {benefitData[benefit.titleKey]}
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      {benefitData[benefit.descKey]}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto text-center">
          {/* Logged-in investor: dashboard shortcut */}
          {user && user.role === 'investor' && (
            <Card
              data-testid="investor-portal-banner"
              className="bg-gradient-to-br from-emerald-500/10 via-[#1a1a1a] to-[#C4A55A]/10 border-[#C4A55A]/40 mb-6"
            >
              <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 py-6">
                <div className="text-left">
                  <p className="text-emerald-300 text-xs uppercase tracking-wider mb-1">
                    Eingeloggt als Investor
                  </p>
                  <h3 className="text-xl font-semibold text-white">
                    Willkommen zurück, {user.firstName}
                  </h3>
                  <p className="text-gray-300 text-sm">
                    Ihr Portfolio + aktuelle Aktien-Übersicht
                  </p>
                </div>
                <Button
                  asChild
                  size="lg"
                  data-testid="investor-portal-cta"
                  className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
                >
                  <Link to="/investor/dashboard">
                    <LayoutDashboard className="mr-2 h-5 w-5" />
                    Mein Portfolio
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a] border-[#C4A55A]/30 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-3xl text-white mb-2">
                Starten Sie Ihre Investment-Reise
              </CardTitle>
              <CardDescription className="text-gray-400 text-lg">
                Registrieren Sie sich jetzt und erhalten Sie Zugang zu exklusiven Investmentmöglichkeiten
              </CardDescription>
              {/* Trust signals */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5 text-left">
                <div className="bg-black/40 border border-[#C4A55A]/15 rounded-lg p-3">
                  <div className="text-[#C4A55A] text-xs uppercase tracking-wider mb-1">Mindesteinlage</div>
                  <div className="text-white font-semibold">Ab CHF 3 000</div>
                  <div className="text-gray-400 text-xs">30 Namen­aktien</div>
                </div>
                <div className="bg-black/40 border border-[#C4A55A]/15 rounded-lg p-3">
                  <div className="text-[#C4A55A] text-xs uppercase tracking-wider mb-1">Eintragung</div>
                  <div className="text-white font-semibold">CH-Aktienregister</div>
                  <div className="text-gray-400 text-xs">das-aktienregister.ch</div>
                </div>
                <div className="bg-black/40 border border-[#C4A55A]/15 rounded-lg p-3">
                  <div className="text-[#C4A55A] text-xs uppercase tracking-wider mb-1">Sicherheit</div>
                  <div className="text-white font-semibold">TLS &amp; bcrypt</div>
                  <div className="text-gray-400 text-xs">HTTP-only Sessions</div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row gap-4 justify-center">
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold px-8 transition-all duration-300 hover:scale-105 group"
                    onClick={() => {
                      setIsLoginMode(false);
                      setIsDialogOpen(true);
                    }}
                  >
                    <UserPlus className="mr-2 h-5 w-5" />
                    {t.investors.cta}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1a1a1a] border-[#C4A55A]/30 max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl text-white">
                      {isLoginMode ? t.investors.loginTitle : t.investors.registerTitle}
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                      {isLoginMode 
                        ? 'Melden Sie sich bei Ihrem Investorenkonto an' 
                        : 'Erstellen Sie Ihr Investorenkonto und starten Sie noch heute'}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    {!isLoginMode && activeRef && (
                      <div
                        data-testid="referral-active-banner"
                        className="px-3 py-2 rounded-md bg-[#C4A55A]/10 border border-[#C4A55A]/40 text-sm text-[#C4A55A]"
                      >
                        Empfohlen von <strong>{activeRef.firstName} {activeRef.lastName}</strong>
                        <span className="text-gray-400"> (Code: {activeRef.code})</span>
                      </div>
                    )}
                    {!isLoginMode && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="firstName" className="text-gray-300">{t.investors.form.firstName}</Label>
                            <Input
                              id="firstName"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleInputChange}
                              className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="lastName" className="text-gray-300">{t.investors.form.lastName}</Label>
                            <Input
                              id="lastName"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleInputChange}
                              className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                              required
                            />
                          </div>
                        </div>
                      </>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-300">{t.investors.form.email}</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                        required
                      />
                    </div>
                    
                    {!isLoginMode && (
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-gray-300">{t.investors.form.phone}</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                          required
                        />
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-gray-300">{t.investors.form.password}</Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                        required
                      />
                    </div>
                    
                    {!isLoginMode && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="confirmPassword" className="text-gray-300">{t.investors.form.confirmPassword}</Label>
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="investment" className="text-gray-300">{t.investors.form.investment}</Label>
                          <Input
                            id="investment"
                            name="investment"
                            type="number"
                            value={formData.investment}
                            onChange={handleInputChange}
                            className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                            required
                          />
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="terms"
                            checked={formData.terms}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, terms: checked }))}
                            className="border-[#C4A55A]/30 data-[state=checked]:bg-[#C4A55A] data-[state=checked]:text-black"
                          />
                          <Label htmlFor="terms" className="text-sm text-gray-400 cursor-pointer">
                            {t.investors.form.terms}
                          </Label>
                        </div>
                      </>
                    )}
                    
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold transition-all duration-300"
                    >
                      {isLoginMode ? t.investors.form.submitLogin : t.investors.form.submit}
                    </Button>
                    
                    <button
                      type="button"
                      onClick={() => setIsLoginMode(!isLoginMode)}
                      className="w-full text-sm text-[#C4A55A] hover:text-[#D4B56A] transition-colors duration-300"
                    >
                      {isLoginMode ? t.investors.form.noAccount : t.investors.login}
                    </button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={isDialogOpen && isLoginMode} onOpenChange={(open) => {
                if (!open) setIsDialogOpen(false);
              }}>
                <DialogTrigger asChild>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-[#C4A55A] text-[#C4A55A] hover:bg-[#C4A55A] hover:text-black transition-all duration-300 hover:scale-105"
                    onClick={() => {
                      setIsLoginMode(true);
                      setIsDialogOpen(true);
                    }}
                  >
                    <LogIn className="mr-2 h-5 w-5" />
                    Login
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1a1a1a] border-[#C4A55A]/30 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-2xl text-white">
                      {t.investors.loginTitle}
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                      Melden Sie sich bei Ihrem Investorenkonto an
                    </DialogDescription>
                  </DialogHeader>
                  
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-gray-300">{t.investors.form.email}</Label>
                      <Input
                        id="login-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-gray-300">{t.investors.form.password}</Label>
                      <Input
                        id="login-password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white focus:border-[#C4A55A]"
                        required
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold transition-all duration-300"
                    >
                      {t.investors.form.submitLogin}
                    </Button>
                    
                    <button
                      type="button"
                      onClick={() => setIsLoginMode(false)}
                      className="w-full text-sm text-[#C4A55A] hover:text-[#D4B56A] transition-colors duration-300"
                    >
                      {t.investors.form.noAccount}
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* External Stock Register Link */}
          <div className="mt-8">
            <a
              href="https://www.das-aktienregister.ch/loewenherz-pool-invest-ag/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-[#C4A55A] hover:text-[#D4B56A] transition-colors duration-300"
            >
              <span className="mr-2">Besuchen Sie unser Aktienregister</span>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};